import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonScreen, CoverPhotoContainer } from './CoverPhoto.styles';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { isInIframe } from '@shared/utils/isInIframe';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex } from '@withjoy/joykit';
import { Add } from '@withjoy/joykit/icons';
import { useQueryParamHelper } from '@shared/core/queryString';
import { withWindow } from '@shared/utils/withWindow';
import { OptimizedEventGuestSitePhoto } from '../OptimizedEventGuestSitePhoto';
import type { PercentCrop } from 'react-image-crop';
import { useTranslation } from '@shared/core';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import type { WebsitePagePhotoPresentationFragment } from '../../../../GuestSite.graphql.generated';
import { isPhotoTombstoned } from '@shared/utils/eventPhoto/isPhotoTombstoned';
import type { PhotoV2WithoutBlurHashFragment } from '@graphql/fragments/photo.graphql.generated';

const _getImageRendition = (url: string) => {
  const imageUrl = new URL(url);
  imageUrl.searchParams.append('rendition', 'medium');
  return imageUrl.toString();
};

const { isTest } = getNodeEnv();

// Synchronous browser-cache probe (mirrors `useProgressiveImage`): true when the image is already
// decoded in memory, so a client-side navigation/reload starts `loaded` and skips the skeleton.
const isImageCached = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const img = new Image();
  img.src = url;
  return img.complete && img.naturalWidth > 0;
};

// False while hydrating the server HTML, true for every later client-side mount (navigation).
// Mirrors useSkipInitialAnimation's latch. Hydration must render `loaded=true` like the server
// did — on a cold cache the probe misses, flipped the screen back to the skeleton, and unmounted
// the server-rendered photo: a double layout swap on first load (header-CLS audit, center layout).
let hasHydrated = false;

export const LazyScreen: React.FC<{
  src?: string;
  height: string | number;
  width?: string | number;
  isFullBleed: boolean;
  isChildEagerLoadRequire?: boolean;
  // `<width> / <height>` — reserves the photo's aspect-ratio box on the skeleton so it matches the
  // loaded photo and the box doesn't shrink-then-grow when `loaded` toggles.
  aspectRatio?: string;
  children: (loaded: boolean) => JSX.Element;
}> = ({ height, width, children, src, isFullBleed, isChildEagerLoadRequire = false, aspectRatio }) => {
  // Probe/preload the medium rendition — the same URL LazyScreen's only consumer (header CenterPhoto)
  // renders via getImageRendition. Do NOT infer the guestSkipPagePhotoRendition kill switch here:
  // CenterPhoto always displays the medium rendition, so probing/loading the raw URL when the flag is
  // on would fetch a full-size image the child never shows and delay the loaded signal. PRNT-4546.
  const [loaded, setLoaded] = useState(() => {
    if (src === undefined || isTest || __SERVER__ || !hasHydrated) {
      return true;
    }
    return isImageCached(_getImageRendition(src));
  });

  useEffect(() => {
    hasHydrated = true;
  }, []);

  const isPreviewing = isInIframe();

  const handeOnLoad = () => {
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded && src) {
      const img = new Image();
      img.onload = handeOnLoad;
      img.src = _getImageRendition(src);
    }
  }, [loaded, src]);

  useEffect(() => {
    if (isPreviewing && loaded) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, loaded]);

  // const memoizedImage = src ? <img alt="" src={src} style={{ display: 'none' }} onLoad={handeOnLoad} /> : null;

  return (
    <SkeletonScreen
      height={height}
      width={width}
      index={0}
      loaded={loaded}
      isFullBleed={isFullBleed}
      aspectRatio={aspectRatio}
    >
      {loaded || isChildEagerLoadRequire ? children(loaded) : null}
    </SkeletonScreen>
  );
};

interface Props
  extends Readonly<{
    photo?: Maybe<PhotoV2WithoutBlurHashFragment>;
    pageId?: string;
    pageType?: EventPageType;
    pageSlug?: string;
    websiteLayoutType: DesignLayoutType;

    // `CoverPhoto` is rendered in `LayoutBrannan` and `LayoutAloha` but only `LayoutBrannan`
    // passes the `photoPresentation` prop. No need to check if the feature flag is enabled.
    photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>;
  }> {}

const FULL_BLEED_IMAGE_PROPERTIES: Record<'parent', BoxProps & { 'data-photo-width': string }> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100vw',
    overflow: 'hidden',
    // All CSS units are converted to values before doing the operation.
    // -50vw: is 50% of the viewport width
    // +50%: is 50% of the *element's* width
    //
    // it's effectively the same as setting `left: -50%; transform: translateX(50%);`
    // but we need to calculate based off the viewport, not the element's width
    left: 'calc(-50vw + 50%)',
    'data-photo-width': 'full',
  },
};

const PAGE_WIDTH_IMAGE_PROPERTIES: Record<'parent', BoxProps> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    left: '0px',
  },
};

const checkIfShouldRenderFullBleedImage = (websiteLayout: DesignLayoutType, photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>) => {
  return websiteLayout === DesignLayoutType.brannan && photoPresentation?.presentationLayout === 'fullWidth';
};
const checkIfShouldRenderPageWidthImage = (websiteLayout: DesignLayoutType, photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>) => {
  return websiteLayout === DesignLayoutType.brannan && photoPresentation?.presentationLayout === 'pageWidth';
};

export const CoverPhoto: React.FC<Props> = props => {
  const { photo: rawPhoto, pageId, pageType, pageSlug, websiteLayoutType, photoPresentation } = props;
  const photo = isPhotoTombstoned(rawPhoto) ? undefined : rawPhoto;
  const { tq } = useTranslation('joykit');

  const enablePhotoLayout = websiteLayoutType === DesignLayoutType.brannan;

  const queryParamHelper = useQueryParamHelper();
  const inlineEditingEnabled = useMemo(() => queryParamHelper.getValue('feature.enableInlineEditing') === 'true', [queryParamHelper]);

  // Kill switch for environments that must not request renditioned page-photo URLs — pass the raw
  // photo.url through to OptimizedEventGuestSitePhoto instead of tiny/xlarge renditions.
  const [isGuestSkipPagePhotoRenditionEnabled] = useIsFeatureEnabled('guestSkipPagePhotoRendition');

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editPhoto',
        inlineEditData: {
          enablePhotoLayout,
          photoPresentation,
          photo: photo,
          pageId: pageId || '',
          page: pageType,
          pageSlug,
        },
      },
    });
  };

  const shouldRenderFullBleedImage = enablePhotoLayout && checkIfShouldRenderFullBleedImage(websiteLayoutType, photoPresentation);
  const shouldRenderPageWidthImage = enablePhotoLayout && checkIfShouldRenderPageWidthImage(websiteLayoutType, photoPresentation);

  // Crop aspect ratio for full-bleed/page-width (the contained presentation shows the whole photo
  // and ignores crop). Mirrors HeaderV2's getHeaderPhotoAspectRatio; defaults to 16/9 when uncropped.
  const computeAspectRatio = useMemo(() => {
    if (photoPresentation?.cropInfo && photo) {
      let cropWidth = photoPresentation.cropInfo.width;
      let cropHeight = photoPresentation.cropInfo.height;
      if (photoPresentation?.cropInfo?.unit === 'percent') {
        cropWidth = (photoPresentation.cropInfo.width / 100) * photo.width;
        cropHeight = (photoPresentation.cropInfo.height / 100) * photo.height;
      }
      const aspectRatio = cropWidth / cropHeight;
      return aspectRatio;
    }
    // retuns default 16/9 if photoPresentation cropInfo is not available
    return 16 / 9;
  }, [photo, photoPresentation?.cropInfo]);

  // OptimizedEventGuestSitePhoto's CSS crop math expects percentages. Page-photo cropInfo may be
  // stored as 'percent' (already 0–100) or 'px' (convert against the photo's natural size).
  const normalizedCropInfo = useMemo<PercentCrop | null>(() => {
    const cropInfo = photoPresentation?.cropInfo;
    if (!cropInfo || !photo) {
      return null;
    }
    if (cropInfo.unit === 'percent') {
      return { unit: '%', x: cropInfo.x, y: cropInfo.y, width: cropInfo.width, height: cropInfo.height };
    }
    return {
      unit: '%',
      x: (cropInfo.x / photo.width) * 100,
      y: (cropInfo.y / photo.height) * 100,
      width: (cropInfo.width / photo.width) * 100,
      height: (cropInfo.height / photo.height) * 100,
    };
  }, [photo, photoPresentation?.cropInfo]);

  // 0 on SSR (window is absent); layout is driven by CSS aspect-ratio, so these pixel dimensions
  // only feed OptimizedEventGuestSitePhoto's canvas fallback.
  const containerWidth = withWindow(window => window.innerWidth, 0);

  // All presentations render through OptimizedEventGuestSitePhoto (progressive low-res → high-res,
  // no skeleton box), so the placeholder is always the exact size of the loaded photo — no
  // shrink-then-grow. Full-bleed/page-width mirror HeaderV2's PageOrScreenWidthImage. PRNT-4546.
  const renderPresentationPhoto = () => {
    if (!photo) {
      return null;
    }

    // Full-bleed (edge-to-edge). Cropped: a crop-AR flex child, centered and clipped by the
    // 100vw/maxHeight box. Uncropped: viewport cover.
    if (shouldRenderFullBleedImage) {
      if (normalizedCropInfo) {
        return (
          <Box {...FULL_BLEED_IMAGE_PROPERTIES.parent}>
            <Box
              width="100%"
              aspectRatio={`${computeAspectRatio}`}
            >
              <OptimizedEventGuestSitePhoto
                photoUrl={photo.url}
                skipRendition={isGuestSkipPagePhotoRenditionEnabled}
                preloadCrossOrigin={false}
                cropInfo={normalizedCropInfo}
                width={containerWidth}
                height={containerWidth / computeAspectRatio}
                containerType="fixed-aspect"
              />
            </Box>
          </Box>
        );
      }
      return (
        <Box
          aspectRatio={`${computeAspectRatio}`}
          {...FULL_BLEED_IMAGE_PROPERTIES.parent}
        >
          <OptimizedEventGuestSitePhoto
            photoUrl={photo.url}
            skipRendition={isGuestSkipPagePhotoRenditionEnabled}
            preloadCrossOrigin={false}
            width={containerWidth}
            height={Math.min(containerWidth / computeAspectRatio, 600)}
            containerType="viewport"
            containerAspectRatio={computeAspectRatio}
          />
        </Box>
      );
    }

    // Page-width (content-column width). Crop-AR box, fixed-aspect cover.
    if (shouldRenderPageWidthImage) {
      return (
        <Box
          aspectRatio={`${computeAspectRatio}`}
          {...PAGE_WIDTH_IMAGE_PROPERTIES.parent}
        >
          <OptimizedEventGuestSitePhoto
            photoUrl={photo.url}
            skipRendition={isGuestSkipPagePhotoRenditionEnabled}
            preloadCrossOrigin={false}
            cropInfo={normalizedCropInfo}
            width={containerWidth}
            height={Math.min(containerWidth / computeAspectRatio, 600)}
            containerType="fixed-aspect"
          />
        </Box>
      );
    }

    // Contained (default): whole photo, letterboxed. CoverPhotoContainer reserves the photo's
    // aspect-ratio box (padding-top hack, capped at 600 on md+); the photo fills it absolutely.
    return (
      <CoverPhotoContainer
        data-width={photo.width}
        data-height={photo.height}
        data-testid={'body-coverphoto'}
        position={'relative'}
        photo={photo}
        maxHeight={[null, null, null, 600]}
        width={'100%'}
        // Inline (not a styled class) on purpose: the padding-top reservation lives in a
        // styled-components class + pseudo-element, so any paint before that CSS applies renders
        // the box near-collapsed (measured 34px tall mid-load on the stacked layout), and the
        // settle to full height shifts everything below it. An inline aspect-ratio ships with the
        // element itself — the box is right-sized in every paint, including pre-stylesheet ones.
        // maxHeight still caps it; the padding-top rule remains as the older-browser fallback.
        // PRNT-4799.
        style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
        >
          <OptimizedEventGuestSitePhoto
            photoUrl={photo.url}
            skipRendition={isGuestSkipPagePhotoRenditionEnabled}
            preloadCrossOrigin={false}
            width={(600 * photo.width) / photo.height}
            height={600}
            containerType="fixed-aspect"
            objectFit="contain"
          />
        </Box>
      </CoverPhotoContainer>
    );
  };

  return (
    <>
      {photo ? (
        <InlineEditor
          key={`inline-edit-photo-${pageId || ''}-${photo?.id || ''}`}
          elementLabel={tq`Photo`}
          wrapperType="actionInside"
          actionData={{
            editPhoto: handleEditPhotoClick,
          }}
          componentName="pagePhoto"
          pageName={pageType}
          pageSlug={pageSlug || ''}
          isEligibleForInlineEditing={pageType !== EventPageType.app}
          wrapperCSS={shouldRenderFullBleedImage ? { width: '100vw', left: 'calc(-50vw + 50%)' } : shouldRenderPageWidthImage ? { width: '100%', left: '0px' } : {}}
        >
          {renderPresentationPhoto()}
        </InlineEditor>
      ) : (
        inlineEditingEnabled && (
          <InlineEditor
            key={`inline-edit-photo-${pageId || ''}-add-new-photo`}
            elementLabel={tq`Photo`}
            wrapperType="emptyChild"
            actionData={{
              addPhoto: handleEditPhotoClick,
            }}
            componentName="pagePhoto"
            pageName={pageType}
            pageSlug={pageSlug || ''}
            isEligibleForInlineEditing={pageType !== EventPageType.app}
            emptyChildHoverOverlay={
              <Flex
                width={32}
                height={32}
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                backgroundColor="ai2"
                color="white"
              >
                <Add size={20} />
              </Flex>
            }
          >
            <Box
              height="100%"
              width="100%"
            />
          </InlineEditor>
        )
      )}
    </>
  );
};
